/**
 * Shared Modal component. Copied from design-lab (frontend/components/jiffy/design-lab/shared/ui/design-system/Modal.tsx).
 * TODO: Create ticket to backport design-lab to use @design-system/Modal and remove the duplicate there.
 */
import type { ReactNode } from "react";
import { useEffect } from "react";
import { ModalOverlay, Modal as RACModal, Dialog, Heading } from "react-aria-components";
import { tv, cn } from "tailwind-variants";
import { X } from "lucide-react";

const COOKIE_BANNER_ID = "iubenda-cs-banner";

const overlayStyles = tv({
  base: "fixed inset-0 isolate z-30 bg-black/[50%] text-center",
  variants: {
    isEntering: {
      true: "animate-in fade-in duration-200 ease-out"
    },
    isExiting: {
      true: "animate-out fade-out duration-200 ease-in"
    }
  }
});

const modalStyles = tv({
  base: "font-sans w-full rounded-2xl bg-white dark:bg-neutral-800/70 dark:backdrop-blur-2xl dark:backdrop-saturate-200 forced-colors:bg-[Canvas] text-left align-middle text-neutral-700 dark:text-neutral-300 shadow-2xl bg-clip-padding border border-black/10 dark:border-white/10 overflow-hidden",
  variants: {
    isEntering: {
      true: "animate-in zoom-in-105 ease-out duration-200"
    },
    isExiting: {
      true: "animate-out zoom-out-95 ease-in duration-200"
    }
  }
});

export type ModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  children: ReactNode;
  className?: string;
  backdropClassName?: string;
  containerClassName?: string;
  closeOnEsc?: boolean;
  closeOnBackdropClick?: boolean;
  title?: string;
  description?: string;
};

export function Modal({
  isOpen,
  onClose,
  children,
  className,
  backdropClassName,
  containerClassName,
  closeOnEsc = true,
  closeOnBackdropClick = true,
  title = "",
  description = ""
}: ModalProps): React.ReactElement {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    if (!open && onClose) {
      onClose();
    }
  };

  const isDismissable = closeOnEsc && closeOnBackdropClick;
  const overlayClassName = cn(overlayStyles(), backdropClassName) ?? "";
  const modalClassName = cn(modalStyles(), className) ?? "";

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={isDismissable}
      className={overlayClassName}
      shouldCloseOnInteractOutside={$el => !$el.closest(`#${COOKIE_BANNER_ID}`)}
    >
      <div
        className={cn(
          "fixed inset-0 box-border flex items-center justify-center",
          containerClassName
        )}
      >
        <RACModal className={modalClassName}>
          <Dialog className="flex h-full flex-col">
            {title && (
              <Heading slot="title" className="sr-only">
                {title}
              </Heading>
            )}
            {description && <p className="sr-only">{description}</p>}
            {children}
          </Dialog>
        </RACModal>
      </div>
    </ModalOverlay>
  );
}

export type ModalHeaderProps = {
  children: ReactNode;
  className?: string;
  titleClassName?: string;
  onClose?: () => void;
};

export function ModalHeader({
  children,
  className,
  titleClassName,
  onClose
}: ModalHeaderProps): React.ReactElement {
  return (
    <div className={cn("relative flex items-center justify-center border-b p-6", className)}>
      <div className={cn("text-lg font-semibold", titleClassName)}>{children}</div>
      {onClose && (
        <div className="absolute right-6 flex">
          <button
            type="button"
            data-testid="modal-close-button"
            onClick={onClose}
            className="cursor-pointer text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <span className="sr-only">Close</span>
            <X className="h-6 w-6" />
          </button>
        </div>
      )}
    </div>
  );
}

export type ModalBodyProps = {
  children: ReactNode;
  className?: string;
};

export function ModalBody({ children, className }: ModalBodyProps): React.ReactElement {
  return <div className={cn("overflow-auto p-4", className)}>{children}</div>;
}

export type ModalFooterProps = {
  children: ReactNode;
  className?: string;
};

export function ModalFooter({ children, className }: ModalFooterProps): React.ReactElement {
  return <div className={cn("border-t p-4", className)}>{children}</div>;
}
